// Browser Firebase client (Auth + Firestore). In local dev (VITE_FIREBASE_EMULATOR="1")
// it connects to the emulators instead of the cloud, so no real project or
// credentials are needed to run and test.
import { initializeApp, getApps, type FirebaseApp } from "firebase/app";
import { getAuth, connectAuthEmulator, type Auth } from "firebase/auth";
import { getFirestore, connectFirestoreEmulator, type Firestore } from "firebase/firestore";

const useEmulator = process.env.VITE_FIREBASE_EMULATOR === "1";

// In emulator mode the SDK only needs a projectId; other values can be dummies.
const firebaseConfig = {
  apiKey: process.env.VITE_FIREBASE_API_KEY || "demo-api-key",
  authDomain: process.env.VITE_FIREBASE_AUTH_DOMAIN || "demo-ivr-local.firebaseapp.com",
  projectId: process.env.VITE_FIREBASE_PROJECT_ID || "demo-ivr-local",
  storageBucket: process.env.VITE_FIREBASE_STORAGE_BUCKET || "demo-ivr-local.appspot.com",
  messagingSenderId: process.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "0",
  appId: process.env.VITE_FIREBASE_APP_ID || "demo-app-id",
};

let _app: FirebaseApp | undefined;
let _auth: Auth | undefined;
let _db: Firestore | undefined;
let _authWired = false;
let _dbWired = false;

function app(): FirebaseApp {
  if (!_app) _app = getApps().find((a) => a.name === "[DEFAULT]") ?? initializeApp(firebaseConfig);
  return _app;
}

export function firebaseAuth(): Auth {
  if (!_auth) _auth = getAuth(app());
  if (useEmulator && !_authWired) {
    // disableWarnings avoids the noisy banner; safe in local dev only.
    connectAuthEmulator(_auth, "http://127.0.0.1:9099", { disableWarnings: true });
    _authWired = true;
  }
  return _auth;
}

/**
 * A second, isolated Auth instance. Used by admins to create another user's
 * login: createUserWithEmailAndPassword signs the NEW user into this instance,
 * leaving the admin's own session (primary instance) untouched.
 */
let _secondaryAuth: Auth | undefined;
export function secondaryAuth(): Auth {
  if (!_secondaryAuth) {
    const sec = getApps().find((a) => a.name === "secondary") ?? initializeApp(firebaseConfig, "secondary");
    _secondaryAuth = getAuth(sec);
    if (useEmulator) connectAuthEmulator(_secondaryAuth, "http://127.0.0.1:9099", { disableWarnings: true });
  }
  return _secondaryAuth;
}

export function firestore(): Firestore {
  if (!_db) _db = getFirestore(app());
  if (useEmulator && !_dbWired) {
    connectFirestoreEmulator(_db, "127.0.0.1", 8080);
    _dbWired = true;
  }
  return _db;
}
